<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">{{ $t('golf_caddies') || 'Caddies' }}</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" rounded elevation="2" @click="openDialog()">
                        <v-icon left>mdi-plus</v-icon>
                        Nuevo Caddie
                    </v-btn>
                </v-card-title>

                <!-- Estadísticas -->
                <v-row class="mb-4">
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold primary--text">{{ caddies.length }}</div>
                            <div class="caption grey--text">Caddies Totales</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold green--text">{{ activeCaddies }}</div>
                            <div class="caption grey--text">Activos</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold orange--text">{{ totalRoundsPlayed }}</div>
                            <div class="caption grey--text">Total Rondas</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="text-center pa-3">
                            <div class="display-1 font-weight-bold info--text">{{ avgRating }}</div>
                            <div class="caption grey--text">Calificación Promedio</div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Búsqueda -->
                <v-card class="mb-4">
                    <v-card-text class="pa-4">
                        <v-text-field
                            v-model="searchQuery"
                            prepend-inner-icon="mdi-magnify"
                            label="Buscar por nombre o apodo..."
                            outlined
                            dense
                            clearable
                        ></v-text-field>
                    </v-card-text>
                </v-card>

                <!-- Tabla de Caddies -->
                <v-card class="elevation-1">
                    <v-card-subtitle class="pa-4">
                        <v-icon small class="mr-2">mdi-account-tie</v-icon>
                        Padrón de Caddies
                    </v-card-subtitle>
                    <v-data-table
                        :headers="headers"
                        :items="filteredCaddies"
                        :loading="loading"
                        class="elevation-0"
                        :no-data-text="'Sin caddies registrados'">

                        <template v-slot:item.photo="{ item }">
                            <v-avatar size="40" class="my-2" color="primary">
                                <span class="white--text font-weight-bold">{{ getInitials(item.name) }}</span>
                            </v-avatar>
                        </template>

                        <template v-slot:item.name="{ item }">
                            <div class="font-weight-bold">{{ item.name }}</div>
                            <div class="caption grey--text">{{ item.nickname }}</div>
                        </template>

                        <template v-slot:item.caddie_type="{ item }">
                            <v-chip small :color="getTypeColor(item.caddie_type)">
                                {{ getCaddieTypeName(item.caddie_type) }}
                            </v-chip>
                        </template>

                        <template v-slot:item.status="{ item }">
                            <v-chip :color="item.status === 'activo' ? 'green' : 'grey'" small text-color="white">
                                {{ item.status === 'activo' ? 'Activo' : 'Inactivo' }}
                            </v-chip>
                        </template>

                        <template v-slot:item.rounds="{ item }">
                            <div class="text-center">
                                <v-chip small color="info" text-color="white">{{ item.rounds_completed }}</v-chip>
                            </div>
                        </template>

                        <template v-slot:item.rating="{ item }">
                            <div class="d-flex align-center justify-center">
                                <v-icon small color="orange" class="mr-1">mdi-star</v-icon>
                                <strong>{{ item.avg_rating.toFixed(1) }}</strong>
                            </div>
                        </template>

                        <template v-slot:item.bonus="{ item }">
                            <v-tooltip v-if="item.rounds_completed >= 30 && item.avg_rating > 4.8" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip small color="success" text-color="white" v-bind="attrs" v-on="on">
                                        <v-icon small left>mdi-gift</v-icon>
                                        Incentivo
                                    </v-chip>
                                </template>
                                <span>30+ rondas con 4.8+ ⭐</span>
                            </v-tooltip>
                            <span v-else class="caption grey--text">-</span>
                        </template>

                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                        <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                                <span>Ver expediente</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="editItem(item)">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>Editar</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                                        <v-icon small color="red">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Eliminar</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- Diálogo para crear/editar caddie (REQ-23) -->
        <v-dialog v-model="dialog" max-width="700px" persistent>
            <v-card>
                <v-card-title>
                    <span class="headline">{{ formTitle }}</span>
                    <v-spacer></v-spacer>
                    <v-icon @click="closeDialog" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="form" v-model="validForm" lazy-validation>
                        <!-- Foto -->
                        <v-row class="mb-4">
                            <v-col cols="12" sm="4" class="text-center">
                                <div class="mb-3">
                                    <v-avatar size="100" :color="editedItem.photo ? 'transparent' : 'primary'">
                                        <img v-if="editedItem.photo" :src="editedItem.photo" alt="Foto">
                                        <span v-else class="white--text display-2 font-weight-bold">
                                            {{ getInitials(editedItem.name) }}
                                        </span>
                                    </v-avatar>
                                </div>
                                <v-file-input
                                    v-model="photoFile"
                                    label="Foto"
                                    prepend-icon="mdi-camera"
                                    outlined
                                    dense
                                    accept="image/*"
                                ></v-file-input>
                            </v-col>

                            <!-- Datos Personales -->
                            <v-col cols="12" sm="8">
                                <div class="subtitle-2 font-weight-bold mb-3">Datos Personales</div>
                                <v-row>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="editedItem.name"
                                            label="Nombre Completo"
                                            outlined
                                            dense
                                            :rules="[v => !!v || 'Nombre es requerido']"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="editedItem.nickname"
                                            label="Apodo/Sobrenombre"
                                            outlined
                                            dense
                                            hint="Campo obligatorio para búsqueda optimizada (REQ-24)"
                                            :rules="[v => !!v || 'Apodo es requerido']"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="editedItem.age"
                                            label="Edad"
                                            type="number"
                                            outlined
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="editedItem.phone"
                                            label="Teléfono"
                                            outlined
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>

                        <!-- Documentos INE -->
                        <v-row class="mb-4">
                            <v-col cols="12">
                                <div class="subtitle-2 font-weight-bold mb-3">Documentos (INE)</div>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-file-input
                                    v-model="inePhotoFront"
                                    label="Foto INE (Frente)"
                                    prepend-icon="mdi-id-card"
                                    outlined
                                    dense
                                    accept="image/*"
                                ></v-file-input>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-file-input
                                    v-model="inePhotoBack"
                                    label="Foto INE (Dorso)"
                                    prepend-icon="mdi-id-card"
                                    outlined
                                    dense
                                    accept="image/*"
                                ></v-file-input>
                            </v-col>
                        </v-row>

                        <!-- Tipo y Estado -->
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="editedItem.caddie_type"
                                    :items="caddieTypes"
                                    item-text="text"
                                    item-value="value"
                                    label="Tipo de Caddie"
                                    outlined
                                    dense
                                    :rules="[v => !!v || 'Tipo es requerido']"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="editedItem.status"
                                    :items="[{ text: 'Activo', value: 'activo' }, { text: 'Inactivo', value: 'inactivo' }]"
                                    label="Estado"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDialog">Cancelar</v-btn>
                    <v-btn color="primary" text @click="save" :disabled="!validForm">Guardar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo de Expediente Completo -->
        <v-dialog v-model="dialogDetails" max-width="700px">
            <v-card v-if="viewItem">
                <v-card-title>
                    <div class="d-flex align-center">
                        <v-avatar size="48" :color="viewItem.photo ? 'transparent' : 'primary'" class="mr-3">
                            <img v-if="viewItem.photo" :src="viewItem.photo" alt="Foto">
                            <span v-else class="white--text font-weight-bold">{{ getInitials(viewItem.name) }}</span>
                        </v-avatar>
                        <div>
                            <div class="headline">{{ viewItem.name }}</div>
                            <div class="caption grey--text">{{ viewItem.nickname }}</div>
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="dialogDetails = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <!-- Datos Personales -->
                    <div class="subtitle-2 font-weight-bold mb-3">Datos Personales</div>
                    <v-row class="mb-4">
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Edad</div>
                            <div class="body-2">{{ viewItem.age }} años</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Teléfono</div>
                            <div class="body-2">{{ viewItem.phone || 'No registrado' }}</div>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Información de Desempeño (REQ-25, REQ-26) -->
                    <div class="subtitle-2 font-weight-bold mb-3">Desempeño y Calificaciones</div>
                    <v-row class="mb-4">
                        <v-col cols="12" sm="6">
                            <div class="text-center pa-3 grey lighten-4 rounded">
                                <div class="display-2 font-weight-bold primary--text">{{ viewItem.rounds_completed }}</div>
                                <div class="caption grey--text">Rondas Completadas</div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="text-center pa-3 grey lighten-4 rounded">
                                <div class="display-2 font-weight-bold orange--text">
                                    <v-icon color="orange">mdi-star</v-icon>
                                    {{ viewItem.avg_rating.toFixed(1) }}
                                </div>
                                <div class="caption grey--text">Calificación Promedio</div>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Alerta de Bono (REQ-26) -->
                    <v-row v-if="viewItem.rounds_completed >= 30 && viewItem.avg_rating > 4.8" class="mb-4">
                        <v-col cols="12">
                            <v-alert type="success" outlined class="mb-0">
                                <v-icon left>mdi-gift</v-icon>
                                <strong>¡Incentivo Activado!</strong>
                                <div class="caption mt-1">
                                    Caddie ha acumulado {{ viewItem.rounds_completed }} rondas con calificación {{ viewItem.avg_rating.toFixed(1) }} ⭐
                                </div>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <!-- Detalle de Calificaciones -->
                    <div class="subtitle-2 font-weight-bold mb-3">Últimas Calificaciones</div>
                    <v-list dense>
                        <v-list-item v-for="(rating, index) in viewItem.recent_ratings" :key="index">
                            <v-list-item-content>
                                <div class="caption">{{ rating.date }}</div>
                                <div class="body-2">Ronda con {{ rating.player }}</div>
                            </v-list-item-content>
                            <v-list-item-action>
                                <div class="d-flex align-center">
                                    <v-icon small color="orange">mdi-star</v-icon>
                                    <strong class="ml-1">{{ rating.stars }}</strong>
                                </div>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>

                    <!-- Información Adicional -->
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Tipo de Caddie</div>
                            <v-chip small :color="getTypeColor(viewItem.caddie_type)">
                                {{ getCaddieTypeName(viewItem.caddie_type) }}
                            </v-chip>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="caption grey--text">Estado</div>
                            <v-chip small :color="viewItem.status === 'activo' ? 'green' : 'grey'" text-color="white">
                                {{ viewItem.status === 'activo' ? 'Activo' : 'Inactivo' }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo de Confirmación de Eliminar -->
        <v-dialog v-model="dialogDelete" max-width="400px">
            <v-card>
                <v-card-title class="headline">Eliminar Caddie</v-card-title>
                <v-card-text>
                    ¿Estás seguro de que deseas eliminar a <strong>{{ itemToDelete?.name }}</strong>?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDelete">Cancelar</v-btn>
                    <v-btn color="red" text @click="deleteItemConfirm">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
export default {
  name: 'GolfCaddies',
  data: () => ({
    dialog: false,
    dialogDetails: false,
    dialogDelete: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    loading: false,
    validForm: false,
    searchQuery: '',
    photoFile: null,
    inePhotoFront: null,
    inePhotoBack: null,
    itemToDelete: null,
    editedIndex: -1,
    viewItem: null,
    headers: [
      { text: 'Foto', value: 'photo', width: '8%', sortable: false },
      { text: 'Nombre / Apodo', value: 'name', width: '25%' },
      { text: 'Tipo', value: 'caddie_type', width: '12%' },
      { text: 'Estado', value: 'status', width: '10%' },
      { text: 'Rondas', value: 'rounds', width: '10%', align: 'center' },
      { text: 'Calificación', value: 'rating', width: '12%', align: 'center' },
      { text: 'Bono', value: 'bonus', width: '12%', align: 'center' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    caddieTypes: [
      { text: 'Caddie Senior', value: 'senior' },
      { text: 'Caddie Intermedio', value: 'intermedio' },
      { text: 'Caddie Junior', value: 'junior' }
    ],
    editedItem: {
      id: null,
      name: '',
      nickname: '',
      age: null,
      phone: '',
      photo: null,
      caddie_type: null,
      status: 'activo',
      rounds_completed: 0,
      avg_rating: 0
    },
    defaultItem: {
      id: null,
      name: '',
      nickname: '',
      age: null,
      phone: '',
      photo: null,
      caddie_type: null,
      status: 'activo',
      rounds_completed: 0,
      avg_rating: 0
    },
    // MOCK DATA - Caddies completos
    caddies: [
      {
        id: 1,
        name: 'José María López',
        nickname: 'Che',
        age: 45,
        phone: '+34 666 123 456',
        photo: 'https://via.placeholder.com/150?text=Che',
        caddie_type: 'senior',
        status: 'activo',
        rounds_completed: 42,
        avg_rating: 4.9,
        recent_ratings: [
          { date: '2025-08-03', player: 'Carlos García', stars: 5 },
          { date: '2025-08-02', player: 'Juan Pérez', stars: 5 },
          { date: '2025-08-01', player: 'Roberto Díaz', stars: 4.5 },
          { date: '2025-07-31', player: 'Fernando Ruiz', stars: 4.8 }
        ]
      },
      {
        id: 2,
        name: 'Manuel Rodríguez García',
        nickname: 'Manu',
        age: 38,
        phone: '+34 666 789 012',
        photo: 'https://via.placeholder.com/150?text=Manu',
        caddie_type: 'intermedio',
        status: 'activo',
        rounds_completed: 28,
        avg_rating: 4.6,
        recent_ratings: [
          { date: '2025-08-03', player: 'Miguel Sánchez', stars: 4.5 },
          { date: '2025-08-02', player: 'Luis Cortés', stars: 4.7 },
          { date: '2025-08-01', player: 'Javier Torres', stars: 4.6 },
          { date: '2025-07-31', player: 'Enrique Vega', stars: 4.5 }
        ]
      },
      {
        id: 3,
        name: 'David Martínez Sánchez',
        nickname: 'David',
        age: 32,
        phone: '+34 666 345 678',
        photo: 'https://via.placeholder.com/150?text=David',
        caddie_type: 'intermedio',
        status: 'activo',
        rounds_completed: 15,
        avg_rating: 4.3,
        recent_ratings: [
          { date: '2025-08-03', player: 'Ana Martínez', stars: 4.3 },
          { date: '2025-08-02', player: 'Carmen López', stars: 4.2 },
          { date: '2025-08-01', player: 'Sofía Ruiz', stars: 4.4 }
        ]
      },
      {
        id: 4,
        name: 'Carlos Hernández López',
        nickname: 'Carlitos',
        age: 26,
        phone: '+34 666 901 234',
        photo: 'https://via.placeholder.com/150?text=Carlitos',
        caddie_type: 'junior',
        status: 'activo',
        rounds_completed: 8,
        avg_rating: 4.7,
        recent_ratings: [
          { date: '2025-08-03', player: 'Raúl Soto', stars: 4.8 },
          { date: '2025-08-01', player: 'Arturo Mendez', stars: 4.6 }
        ]
      },
      {
        id: 5,
        name: 'Antonio Ruiz Pérez',
        nickname: 'Toño',
        age: 52,
        phone: '+34 666 567 890',
        photo: null,
        caddie_type: 'senior',
        status: 'inactivo',
        rounds_completed: 35,
        avg_rating: 4.5,
        recent_ratings: []
      }
    ]
  }),
  computed: {
    filteredCaddies () {
      if (!this.searchQuery) return this.caddies
      const query = this.searchQuery.toLowerCase()
      return this.caddies.filter(c =>
        c.name.toLowerCase().includes(query) || c.nickname.toLowerCase().includes(query)
      )
    },
    formTitle () {
      return this.editedIndex === -1 ? 'Nuevo Caddie' : 'Editar Caddie'
    },
    activeCaddies () {
      return this.caddies.filter(c => c.status === 'activo').length
    },
    totalRoundsPlayed () {
      return this.caddies.reduce((sum, c) => sum + c.rounds_completed, 0)
    },
    avgRating () {
      const activeWithRounds = this.caddies.filter(c => c.rounds_completed > 0)
      if (activeWithRounds.length === 0) return 0
      const avgSum = activeWithRounds.reduce((sum, c) => sum + c.avg_rating, 0)
      return (avgSum / activeWithRounds.length).toFixed(1)
    }
  },
  methods: {
    getInitials (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    getCaddieTypeName (type) {
      const types = {
        senior: 'Caddie Senior',
        intermedio: 'Caddie Intermedio',
        junior: 'Caddie Junior'
      }
      return types[type] || 'Desconocido'
    },
    getTypeColor (type) {
      const colors = {
        senior: 'blue',
        intermedio: 'orange',
        junior: 'green'
      }
      return colors[type] || 'grey'
    },
    openDialog () {
      this.editedIndex = -1
      this.editedItem = { ...this.defaultItem }
      this.photoFile = null
      this.dialog = true
    },
    editItem (item) {
      this.editedIndex = this.caddies.indexOf(item)
      this.editedItem = { ...item }
      this.dialog = true
    },
    viewDetails (item) {
      this.viewItem = { ...item }
      this.dialogDetails = true
    },
    confirmDelete (item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },
    closeDialog () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },
    closeDelete () {
      this.dialogDelete = false
      this.itemToDelete = null
    },
    save () {
      if (this.$refs.form.validate()) {
        if (this.editedIndex > -1) {
          this.$set(this.caddies, this.editedIndex, this.editedItem)
          this.snackbarMessage = 'Caddie actualizado correctamente'
        } else {
          this.editedItem.id = Math.max(...this.caddies.map(c => c.id), 0) + 1
          this.caddies.push(this.editedItem)
          this.snackbarMessage = 'Caddie agregado correctamente'
        }
        this.snackbarColor = 'success'
        this.snackbar = true
        this.closeDialog()
      }
    },
    deleteItemConfirm () {
      const index = this.caddies.indexOf(this.itemToDelete)
      if (index > -1) {
        this.caddies.splice(index, 1)
      }
      this.snackbarMessage = 'Caddie eliminado correctamente'
      this.snackbarColor = 'success'
      this.snackbar = true
      this.closeDelete()
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
